import { Control, Controller } from 'react-hook-form';
import { RadioGroup, styled, Grid } from '@mui/material';
import RadioWithInput from 'components/RadioWithInput';
import { FC } from 'react';
import Autocomplete from 'components/FormElements/AppAutocomplete';
import TextField from 'components/FormElements/AppTextInput';
import SelectionHelperText from 'components/SelectionHelperText';

const StyledLabelSpan = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

type SelectionWithRadioProps = {
  control: Control<any>;
  isNewFieldName: string;
  selectionFieldName: string;
  existingLabel: string;
  newLabel: string;
  newHelperText: string;
  selectBoxProps: {
    data: Array<any>;
    placeholder: string;
    valueKey: string;
    labelKey: string;
    disabled: boolean;
    isLoading?: boolean;
    error?: boolean;
    helperText?: string;
  };
  onSelectionChange: (value: string) => void;
  onNewSelection: () => void;
};

const SelectionWithRadio: FC<SelectionWithRadioProps> = ({
  control,
  isNewFieldName,
  selectionFieldName,
  existingLabel,
  newLabel,
  newHelperText,
  selectBoxProps,
  onSelectionChange,
  onNewSelection,
}) => {
  return (
    <>
      <Controller
        name={isNewFieldName}
        control={control}
        defaultValue={false}
        render={({ field: { value, onChange, ...rest } }) => (
          <RadioGroup
            {...rest}
            value={value}
            onChange={(e) => onChange(e.target.value === 'true')}
          >
            <Grid container spacing={{ xs: 6 }}>
              <RadioWithInput
                value="false"
                label={<StyledLabelSpan>{existingLabel}</StyledLabelSpan>}
                inputComponent={
                  <Controller
                    name={selectionFieldName}
                    control={control}
                    render={({ field: { value }, fieldState: { error } }) => (
                      <Autocomplete
                        key={selectBoxProps.data.length}
                        id={selectionFieldName}
                        value={
                          selectBoxProps.data.find(
                            (item) => item[selectBoxProps.valueKey] === value,
                          ) ?? null
                        }
                        onChange={(_, newValue) => {
                          onSelectionChange(
                            newValue?.[selectBoxProps.valueKey] ?? '',
                          );
                        }}
                        disabled={
                          selectBoxProps.disabled || selectBoxProps.isLoading
                        }
                        loading={selectBoxProps.isLoading}
                        fullWidth
                        options={selectBoxProps.data}
                        placeholder={selectBoxProps.placeholder}
                        getOptionKey={(option) =>
                          option[selectBoxProps.valueKey]
                        }
                        getOptionLabel={(option) =>
                          option[selectBoxProps.labelKey] ?? ''
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={!!error}
                            helperText={error?.message}
                            placeholder={
                              selectBoxProps.isLoading
                                ? 'Loading...'
                                : selectBoxProps.placeholder
                            }
                          />
                        )}
                      />
                    )}
                  />
                }
              />

              <RadioWithInput
                value="true"
                label={newLabel}
                onRadioClick={() => {
                  onNewSelection();
                }}
                inputComponent={
                  <SelectionHelperText>{newHelperText}</SelectionHelperText>
                }
              />
            </Grid>
          </RadioGroup>
        )}
      />
    </>
  );
};

export default SelectionWithRadio;
